<template>
  <div
    v-if="data.showPage"
    class="i-coaching-lifecycle"
  >
    <iBackground color="white" />

    <iCampaignCountdown
      ref="campaignCountdown"
      position="sticky"
      :timer-delay-ms="INITIAL_DELAY"
      :timer-duration-min="data.timerDurationMin"
      @cta-clicked="onCtaClick()"
    />

    <div class="content">
      <div class="caption">{{ data.pageData.headerOne }}</div>
      <div class="info">{{ data.pageData.bodyOne }}</div>

      <div class="video">
        <iframe
          id="vimeo-iframe"
          :src="`${data.pageData.videoUrl}?badge=0&amp;autoplay=1&amp;muted=1&amp;player_id=0&amp;app_id=58479`"
          frameborder="0"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%"
        ></iframe>
      </div>

      <div class="cta">
        <iBaseButton
          v-bind="BUTTON_STYLE"
          text="Check Availability"
          @click="onCtaClick()"
        />
        <div class="slots">
          <div class="pulse-circle"></div>
          <strong>{{ openSlots }} slots</strong> left with exclusive discount
        </div>
      </div>

      <div
        v-if="data.showRemainingContent"
        class="extra"
      >
        <iCampaignRoadmap
          :price="data.pageData.price"
          :header="data.pageData.headerTwo"
          :sub="data.pageData.bodyTwo"
          :product-title="data.pageData.productTitle"
          :product-info="data.pageData.productInfo"
          :coach="data.pageData.coach"
          @cta-clicked="onCtaClick()"
        />
        <div class="select-coach">
          No sessions left?
          <span
            class="redirect"
            @click="goToSelectCoach"
          >
            Meet another expert ready to help.
          </span>
        </div>
        <div ref="embedCalendarRef">
          <iCalcomScheduler
            :on-booking-successful="getPaymentAction([data.pageData.stripePriceUuid], UserFlow.COACHING)"
            :embed-link="
              UrlService.generateCalComEmbedLink(
                `${data.pageData.coach?.calcomUsername}/${data.pageData.calcomEventTypeSlug}`
              )
            "
          />
        </div>
        <div class="money-back">
          <div class="header"><img :src="iconSheild" />100% Money-Back Guarantee</div>
          <div class="info">
            If you are unhappy with your session,<br />I will refund your money. No question asked.
          </div>
          <div class="copyright">
            Please read our
            <a
              href="https://www.jobtest.org/refund-policy"
              target="_blank"
              >Refund Policy</a
            >
            for more details.
          </div>
          <div class="copyright">Copyright © {{ new Date().getFullYear() }} Jobtest.org.<br />All Rights Reserved</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from "@/stores/user"
import { azureRadiance200, azureRadiance700, white } from "@/styles/colors"
import { onMounted, reactive, ref } from "vue"
import { useRoute, useRouter } from "vue-router"

import { GlobalVariables } from "@/helpers/globalVariables"
import api from "@/services/apiClient"
import UrlService from "@/services/urlService"

import getPaymentAction from "@/composables/getPaymentAction"
import useRandomNumber from "@/composables/useRandomNumber"

import iBaseButton from "@/components/base/BaseButton.vue"
import iCampaignCountdown from "@/components/coaching/lifecycle/CampaignCountdown.vue"
import iCampaignRoadmap from "@/components/coaching/lifecycle/CampaignRoadmap.vue"
import iBackground from "@/components/global/Background.vue"
import iCalcomScheduler from "@/components/global/CalcomScheduler.vue"

// ASSETS
import iconSheild from "@/assets/icons/icon-shield-tick.svg"

// TYPES
import type CoachGetLifecyclePageOut from "@/shared/dtos/out/CoachGetLifecyclePageOut"
import { UserFlow } from "@/services/gtmService"

// ROUTES
const router = useRouter()
const activeRoute = useRoute()
const pageSlug = activeRoute.params["slug"] as string

// STORES
const userStore = useUserStore()

//COMPOSABLES
const openSlots = useRandomNumber(userStore.getFullName || "Full Name", 3, 5)

//REFS
const embedCalendarRef = ref<HTMLElement | null>(null)
const campaignCountdown = ref<HTMLElement | null>(null)

// CONST
const INITIAL_DELAY = 60000
const BUTTON_STYLE = {
  backgroundColor: azureRadiance700,
  borderRadius: "8px",
  fontWeight: "500",
  size: "medium",
  textColor: white,
  width: "100%",
  pulseColor: azureRadiance200,
  padding: "16px 24px"
}

// DATA
const data = reactive({
  timerDurationMin: 15,
  showRemainingContent: false,
  pageData: {} as CoachGetLifecyclePageOut,
  showPage: false
})

// FUNCTIONS
const scrollToEmbed = () => {
  const element = embedCalendarRef.value
  if (element) {
    const elementPosition = element.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.scrollY - 76

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth"
    })
  }
}

const onCtaClick = () => {
  if (!data.showRemainingContent) {
    // Force show remaining content of body
    data.showRemainingContent = true

    // Force show the report countdown
    if (campaignCountdown.value) (campaignCountdown.value as any)?.makeActive?.()

    // Scroll to embed after a second delay to allow calendar load
    setTimeout(() => {
      scrollToEmbed()
    }, 1000)
  } else scrollToEmbed()
}

const goToSelectCoach = () => {
  router.push({
    name: userStore.isExecutiveCoachingPotential
      ? GlobalVariables.urls.coaching.children.chooseCoach.routeName
      : GlobalVariables.urls.coaching.children.selectCoach.routeName,
    query: { tab: 3 }
  })
}

//LIFECYCLE HOOKS
onMounted(async () => {
  try {
    data.pageData = await api.coach.getLifecycle(pageSlug)
    data.showPage = true
    setTimeout(() => (data.showRemainingContent = true), INITIAL_DELAY)
  } catch (error) {
    router.push({ name: GlobalVariables.urls.coaching.children.home.routeName })
  }
})
</script>

<style scoped lang="scss">
.i-coaching-lifecycle {
  overflow: hidden;

  .content {
    max-width: 1280px;
    margin: auto 10px;
    padding-top: 20px;

    @media screen and (min-width: $breakpoint-md) {
      margin: auto;
      padding-top: 30px;
      padding-bottom: 50px;
    }

    .caption {
      @include display-sm;

      font-weight: 700;
      text-align: center;

      @media screen and (min-width: $breakpoint-md) {
        @include display-lg;
      }
    }

    .info {
      @include text-sm;

      font-weight: 500;
      color: $black500;
      margin-top: 5px;
      text-align: center;

      @media screen and (min-width: $breakpoint-sm) {
        @include text-lg;

        margin-top: 10px;
      }
    }

    .video {
      max-width: 980px;
      padding: 45% 0 0;
      position: relative;
      margin: 25px auto;
    }

    .cta {
      width: 100%;
      max-width: 500px;
      margin: auto;

      .slots {
        @include flex(row, $gap: 6px);

        width: 85%;
        text-align: center;
        margin: 15px auto 0;

        @media screen and (min-width: $breakpoint-sm) {
          width: 70%;
        }

        .pulse-circle {
          width: 10px;
          height: 10px;

          @include border-radius-rounded;

          background-color: $sorbus500;
          position: relative;

          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgb(255 146 10 / 20%);
            }

            50% {
              box-shadow: 0 0 0 5px rgb(255 146 10 / 20%);
            }

            100% {
              box-shadow: 0 0 0 0 rgb(255 146 10 / 20%);
            }
          }

          &::after {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            background: transparent;

            @include border-radius-rounded;

            animation: pulse 2s forwards infinite;
            animation-timing-function: linear;
          }
        }
      }
    }

    .extra {
      animation: fade-in-animation ease 3s;
      animation-iteration-count: 1;
      animation-fill-mode: forwards;

      .select-coach {
        margin-bottom: 40px;
        text-align: center;

        .redirect {
          font-weight: 700;
          text-decoration: underline;
          cursor: pointer;
          color: $black700;

          &:hover {
            color: $black950;
          }
        }
      }

      .money-back {
        @include flex($direction: column, $gap: 14px);

        margin-top: 40px;
        background-color: $azure-radiance100;
        padding: 15px;
        text-align: center;
        width: 100%;

        @media screen and (min-width: $breakpoint-md) {
          padding: 35px 25px;

          @include border-radius-lg;
        }

        .header {
          @include flex($direction: column, $gap: 8px);
          @include text-lg;

          font-weight: 700;

          @media screen and (min-width: $breakpoint-md) {
            gap: 12px;

            @include display-sm;
          }
        }

        .info {
          @include text-sm;

          font-weight: 500;

          @media screen and (min-width: $breakpoint-md) {
            @include text-lg;
          }
        }

        .copyright {
          font-size: 10px;
          line-height: 16px;
          font-weight: 400;
          color: $black500;

          @media screen and (min-width: $breakpoint-md) {
            @include text-xs;

            font-weight: 400;
          }
        }
      }
    }

    @keyframes fade-in-animation {
      0% {
        opacity: 0;
      }

      100% {
        opacity: 1;
      }
    }
  }
}
</style>
